/**
 * Generic, framework-agnostic color helpers used across the application.
 *
 * Supports `rgb(r,g,b)` and `#rgb` / `#rrggbb` notations as input. Outputs are
 * always returned as `rgb(r,g,b)` strings to keep downstream consumers (e.g.
 * SVG fills) consistent with the existing palette definitions.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface HSL {
  /** Hue in degrees (0-360). */
  h: number;
  /** Saturation (0-1). */
  s: number;
  /** Lightness (0-1). */
  l: number;
}

export const COLOR_LIGHT_TEXT = 'rgb(255,255,255)';
export const COLOR_DARK_TEXT = 'rgb(32,32,32)';

const RGB_PATTERN = /^rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)/;
const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseColor(color: string | null | undefined): RGB | null {
  if (!color) {
    return null;
  }

  const trimmed = color.trim();
  const rgbMatch = trimmed.match(RGB_PATTERN);

  if (rgbMatch) {
    return clampRgb({ r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) });
  }

  const hexMatch = trimmed.match(HEX_PATTERN);

  if (hexMatch) {
    const hex = hexMatch[1];
    const expanded = hex.length === 3 ? hex.split('').map(char => char + char).join('') : hex;
    return {
      r: parseInt(expanded.substring(0, 2), 16),
      g: parseInt(expanded.substring(2, 4), 16),
      b: parseInt(expanded.substring(4, 6), 16),
    };
  }

  return null;
}

export function formatRgb(rgb: RGB): string {
  const { r, g, b } = clampRgb(rgb);
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

/**
 * De dekkende kleur die `color` op dekking `alpha` boven `backdrop` oplevert. Voor prints:
 * printerdrivers vlakken PDF-transparantie slecht af. Onleesbare kleuren geven `color` terug.
 */
export function blendOver(color: string, backdrop: string, alpha: number): string {
  const top = parseColor(color);
  const bottom = parseColor(backdrop);
  if (top == null || bottom == null) return color;
  const mix = (a: number, b: number) => a * alpha + b * (1 - alpha);
  return formatRgb({ r: mix(top.r, bottom.r), g: mix(top.g, bottom.g), b: mix(top.b, bottom.b) });
}

/** WCAG relative luminance (0-1) from sRGB channels. */
export function getRelativeLuminance(rgb: RGB): number {
  const toLinear = (channel: number): number => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

/**
 * Returns whether the color is perceived as "light". The default threshold of
 * 0.5 sits roughly between mid-grey and white in luminance space and matches
 * the boundary used by the bodygraph dynamic gate-circle palette.
 */
export function isLightColor(color: string | RGB, threshold = 0.5): boolean {
  const rgb = typeof color === 'string' ? parseColor(color) : color;
  return rgb ? getRelativeLuminance(rgb) > threshold : false;
}

/**
 * Picks a readable text color (light or dark) for the given background.
 * Falls back to dark text when the input cannot be parsed.
 */
export function getContrastTextColor(background: string | RGB | null | undefined, threshold = 0.4): string {
  if (background === null || background === undefined) {
    return COLOR_DARK_TEXT;
  }

  const rgb = typeof background === 'string' ? parseColor(background) : background;

  if (!rgb) {
    return COLOR_DARK_TEXT;
  }

  return getRelativeLuminance(rgb) > threshold ? COLOR_DARK_TEXT : COLOR_LIGHT_TEXT;
}

/** Returns a copy of the color with its HSL lightness forced to `lightness` (0-1). */
export function withLightness(color: string, lightness: number): string {
  const rgb = parseColor(color);

  if (!rgb) {
    return color;
  }

  const hsl = rgbToHsl(rgb);
  return formatRgb(hslToRgb({ ...hsl, l: clamp01(lightness) }));
}

/** Convenience helpers — keep the lightness anchors in one place. */
export function toVeryLightVariant(color: string, lightness = 0.92): string {
  return withLightness(color, lightness);
}

export function toVeryDarkVariant(color: string, lightness = 0.15): string {
  return withLightness(color, lightness);
}

export function rgbToHsl(rgb: RGB): HSL {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) {
    return { h: 0, s: 0, l };
  }

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h: number;

  switch (max) {
    case r:
      h = ((g - b) / delta) + (g < b ? 6 : 0);
      break;
    case g:
      h = ((b - r) / delta) + 2;
      break;
    default:
      h = ((r - g) / delta) + 4;
  }

  return { h: h * 60, s, l };
}

export function hslToRgb(hsl: HSL): RGB {
  const { h, s, l } = hsl;

  if (s === 0) {
    const value = Math.round(l * 255);
    return { r: value, g: value, b: value };
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = (((h % 360) + 360) % 360) / 360;

  return {
    r: Math.round(hueToRgb(p, q, hk + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, hk) * 255),
    b: Math.round(hueToRgb(p, q, hk - 1 / 3) * 255),
  };
}

function hueToRgb(p: number, q: number, t: number): number {
  let normalized = t;

  if (normalized < 0) {
    normalized += 1;
  }

  if (normalized > 1) {
    normalized -= 1;
  }

  if (normalized < 1 / 6) {
    return p + (q - p) * 6 * normalized;
  }

  if (normalized < 1 / 2) {
    return q;
  }

  if (normalized < 2 / 3) {
    return p + (q - p) * (2 / 3 - normalized) * 6;
  }

  return p;
}

function clampRgb(rgb: RGB): RGB {
  return {
    r: clampChannel(rgb.r),
    g: clampChannel(rgb.g),
    b: clampChannel(rgb.b),
  };
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, value));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
